import React, { useCallback } from "react";
import {
  Divider,
  Popover,
  Flex,
  Icon,
  Type,
  useMediaQuery,
} from "discover.medical.react-component-library";
import {
  DrugInteractionsResultsItemContainer,
  SeverityIndexLabel,
  SeeDetailsButton,
} from "./styled";
import InteractionWarningLabel from "../InteractionWarningLabel";
import PropTypes from "prop-types";

const DrugInteractionResultsItem = ({
  interaction,
  handleItemClick,
  currentIndex,
  totalItems,
  severity,
}) => {
  const {
    seeDetails,
    warningText,
    monographId,
    interactingDrug,
    documentationDisplayLabel,
  } = interaction;

  const interactingDrugDetails = {
    monographId,
    severity,
    apiPath: seeDetails?.functionArgs?.apiPath,
    interactingDrugName: interactingDrug?.name?.toLowerCase(),
  };

  const isSeeDetailsExist = !!seeDetails;

  const isMobile = useMediaQuery("max", "sm");
  const severityToggleType = isMobile ? "click" : "hover";

  const stopPropagation = useCallback((e) => isMobile && e.stopPropagation(), [
    isMobile,
  ]);

  const onItemClick = () => {
    handleItemClick(interactingDrugDetails);
  };

  const severityLabel = (
    <Flex container align={{ item: "center" }}>
      <SeverityIndexLabel
        fontSize="xs"
        fontWeight="semibold"
        textTransform="uppercase"
        marginRight="sm"
      >
        {severity.title}
      </SeverityIndexLabel>
      <Icon
        icon={severity.icon}
        background={{ color: severity.icon.background }}
      />
    </Flex>
  );

  return (
    <>
      <DrugInteractionsResultsItemContainer
        data-testid={`di-result-item-${currentIndex}`}
        $isClickable={isSeeDetailsExist}
        onClick={isSeeDetailsExist ? onItemClick : null}
      >
        <Flex container justify={{ content: "space-between" }}>
          <Flex
            cell
            align={{ self: "stretch" }}
            children={
              <Type element="h4" styledAs="h5" fontWeight="bold">
                {interactingDrug.name}
              </Type>
            }
          />
          <Flex
            cell
            children={
              <Flex justify={{ content: "flex-end" }} onClick={stopPropagation}>
                <Popover
                  placement="right"
                  toggle={severityToggleType}
                  trigger={severityLabel}
                >
                  {severity.description}
                </Popover>
              </Flex>
            }
          />
        </Flex>

        <Type element="p">
          <Type element="span" textTransform="uppercase">
            {documentationDisplayLabel}
          </Type>
        </Type>

        <Type>
          <InteractionWarningLabel content={warningText} />{" "}
          {isSeeDetailsExist && (
            <SeeDetailsButton kind="anchor">
              {seeDetails.label}
            </SeeDetailsButton>
          )}
        </Type>
      </DrugInteractionsResultsItemContainer>
      {currentIndex !== totalItems - 1 && <Divider />}
    </>
  );
};

DrugInteractionResultsItem.displayName = "DrugInteractionResultsItem";

DrugInteractionResultsItem.propTypes = {
  severity: PropTypes.shape({
    severityIndex: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    icon: PropTypes.shape({
      glyph: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      background: PropTypes.string.isRequired,
      size: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  interaction: PropTypes.shape({
    warningText: PropTypes.string.isRequired,
    monographId: PropTypes.number.isRequired,
    documentationDisplayLabel: PropTypes.string.isRequired,
    seeDetails: PropTypes.shape({
      label: PropTypes.string.isRequired,
      functionArgs: PropTypes.shape({
        apiPath: PropTypes.string.isRequired,
      }),
    }),
    interactingDrug: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }),
  }),
  handleItemClick: PropTypes.func.isRequired,
  currentIndex: PropTypes.number.isRequired,
  totalItems: PropTypes.number.isRequired,
};

export default DrugInteractionResultsItem;
